import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {useFicha21} from '../../../context/UserModulesContext';
import {Spinner} from '../../Spinner';

// Ingredientes que NO se muestran como faltantes en el meter porque no hay dónde resolverlos. Hasta
// la Economía del cliente (tanda del 25/09) era siempre el caso de "margen": se le contaba al agente
// en el chat, y una fila gris sin CTA parecía una conexión pendiente (feedback fede 2026-07-06). Con
// el módulo y la ficha 2.1, el margen se carga en `/client/:id/economia` y pasa a ser accionable;
// sin alguno de los dos sigue escondido (la pantalla no existe en el clásico).
const NON_ACTIONABLE_INGREDIENTS = new Set(['margen']);

// Ingredientes que NO se pintan en el medidor por ser redundantes con los que ya se muestran:
// `paid_media` es un superset — si el cliente tiene Google Ads / Meta / TikTok / LinkedIn, ya se
// listan por separado, así que el chip genérico "Paid media" solo sumaba ruido (feedback fede
// 2026-07-06). Se sigue usando para el gating de los prompts cross-canal, solo no se muestra.
const HIDDEN_FROM_METER = new Set(['paid_media']);

// CTA por ingrediente faltante (D8): a dónde se resuelve cada uno. Los de plan
// (cambios_plataformas) no tienen destino self-service — el nudge es comercial.
const INGREDIENT_CTA_ROUTE = {
    google_ads: 'conections',
    meta: 'conections',
    tiktok: 'conections',
    linkedin: 'conections',
    ga4: 'conections',
    ecommerce: 'conections',
    paid_media: 'conections',
    memoria_reuniones: 'memory',
    docs_drive: 'memory',
    objetivos: 'monitoring',
    presupuestos: 'monitoring',
    margen: 'economia',
};

// Medidor de contexto del tab IA (D8 del SSOT de UX): "tu IA con este cliente ve X".
// La pieza que cierra el loop del GTM — cada ingrediente faltante muestra cuántos prompts
// desbloquea (si es lo ÚNICO que le falta a ese prompt) o acerca. Datos: el mismo GET de la
// biblioteca (available_ingredients + required/enhancing por prompt), sin endpoint nuevo.
// `compact` (card del Panel): lo conectado se muestra como chips en línea (no una fila por
// ingrediente — 9 filas de ✓ ahogaban la card) y solo lo FALTANTE queda como fila accionable.
// `hideTitle`: el contenedor ya tiene su propio header (evita el doble "Tu IA con este cliente").
export function IAContextMeter({clientId, library = null, compact = false, hideTitle = false}) {
    const {t} = useTranslation();
    const {ficha21, clientEconomicsEnabled} = useFicha21();
    const economicsActionable = ficha21 === true && clientEconomicsEnabled === true;
    const [loading, setLoading] = useState(library === null);
    const [failed, setFailed] = useState(false);
    const [available, setAvailable] = useState((library && library.available_ingredients) || []);
    const [prompts, setPrompts] = useState((library && library.prompts) || []);

    const fetchLibrary = useCallback(() => {
        // Con `library` pre-fetcheada por el padre (ej. IAPreviewBox del Panel, que también
        // necesita los prompts) no se repite el request.
        if (library !== null) return;
        const url = `${API_URL}agent/prompt-library/?agency_client_id=${clientId}`;
        // `cache: true` para compartir el request con la banda «Usa tu IA» del Recorrido, que
        // consume el MISMO inventario (`available_ingredients`) y se monta en esta misma pantalla.
        // Sin el flag las dos superficies piden lo mismo por separado; con él, el segundo se
        // engancha al request en vuelo. Es el uso para el que existe el flag (arranque de pantalla).
        new APIClient(url).call('GET', {}, {cache: true}).then((data) => {
            if (data && data.available_ingredients) {
                setAvailable(data.available_ingredients);
                setPrompts(data.prompts || []);
            } else {
                setFailed(true);
            }
            setLoading(false);
        });
    }, [clientId, library]);

    useEffect(() => {
        fetchLibrary();
    }, [fetchLibrary]);

    // Ingredientes faltantes que algún prompt referencia. "Desbloquea" solo cuenta prompts cuyo
    // ÚNICO required faltante es ese (conseguirlo los habilita de verdad); si el prompt tiene
    // varios faltantes, este ingrediente lo "acerca" (enhances) — no sobre-prometer.
    const missing = useMemo(() => {
        const availableSet = new Set(available);
        const counts = {};
        const bump = (ing, field) => {
            counts[ing] = counts[ing] || {unlocks: 0, enhances: 0};
            counts[ing][field] += 1;
        };
        prompts.forEach((p) => {
            const missingRequired = (p.required_ingredients || []).filter((ing) => !availableSet.has(ing));
            missingRequired.forEach((ing) => {
                bump(ing, missingRequired.length === 1 ? 'unlocks' : 'enhances');
            });
            (p.enhancing_ingredients || []).forEach((ing) => {
                if (!availableSet.has(ing)) bump(ing, 'enhances');
            });
        });
        const hidden = (ing) => HIDDEN_FROM_METER.has(ing)
            || (NON_ACTIONABLE_INGREDIENTS.has(ing) && !economicsActionable);
        return Object.entries(counts)
            .filter(([ing]) => !hidden(ing))
            .map(([ing, c]) => ({ing, ...c}))
            .sort((a, b) => (b.unlocks - a.unlocks) || (b.enhances - a.enhances));
    }, [available, prompts, economicsActionable]);

    if (loading) {
        return <div className="box-loader"><Spinner color="positive small"/></div>;
    }
    if (failed) return null; // sin datos, el panel no aporta — colapsa en silencio

    const ingredientLabel = (ing) => t(`prompt_library.ingredients.${ing}`, ing);
    // Lo disponible que SÍ se muestra (sin los ingredientes redundantes). El gating de los
    // prompts sigue usando `available` completo — esto es solo presentación.
    const visibleAvailable = available.filter((ing) => !HIDDEN_FROM_METER.has(ing));

    return (
        <div className={`ia-meter ${compact ? '-compact' : ''}`}>
            {!hideTitle && <h4 className="ia-meter-title">{t('ia_tab.meter.title')}</h4>}
            {compact && visibleAvailable.length > 0 && (
                <div className="ia-meter-chips">
                    {/* Cap de chips en la card del Panel: 9 tildes verdes ahogaban la card
                        (mockup ficha v2). El detalle completo vive en el tab IA. */}
                    {visibleAvailable.slice(0, 6).map((ing) => (
                        <span key={ing} className="ia-meter-chip">
                            <i className="fas fa-check"/> {ingredientLabel(ing)}
                        </span>
                    ))}
                    {visibleAvailable.length > 6 && (
                        <span className="ia-meter-chip -more"
                              title={visibleAvailable.slice(6).map(ingredientLabel).join(' · ')}>
                            +{visibleAvailable.length - 6}
                        </span>
                    )}
                </div>
            )}
            <ul className="ia-meter-list">
                {!compact && visibleAvailable.map((ing) => (
                    <li key={ing} className="ia-meter-item -ok">
                        <i className="fas fa-check"/> {ingredientLabel(ing)}
                    </li>
                ))}
                {missing.map(({ing, unlocks, enhances}) => {
                    const route = INGREDIENT_CTA_ROUTE[ing];
                    return (
                        <li key={ing} className="ia-meter-item -missing">
                            <i className="fas fa-circle -hollow"/>
                            {route
                                ? <Link to={`/client/${clientId}/${route}`}>{ingredientLabel(ing)}</Link>
                                : ingredientLabel(ing)}
                            <span className="ia-meter-unlock">
                                {unlocks > 0
                                    ? t('ia_tab.meter.unlocks', {count: unlocks})
                                    : t('ia_tab.meter.enhances', {count: enhances})}
                            </span>
                        </li>
                    );
                })}
            </ul>
            {!compact && <p className="ia-meter-hint">{t('ia_tab.meter.hint')}</p>}
        </div>
    );
}
